/**
 * Utility functions for exporting data to various formats
 */

/**
 * Converts an array of objects to CSV format
 * @param data - Array of objects to convert
 * @param headers - Optional custom headers. If not provided, uses object keys
 * @returns CSV string
 */
export function convertToCSV<T extends Record<string, any>>(
  data: T[],
  headers?: { key: keyof T; label: string }[]
): string {
  if (data.length === 0) return '';

  // Determine headers
  let csvHeaders: string[];
  let keys: (keyof T)[];

  if (headers) {
    csvHeaders = headers.map(h => h.label);
    keys = headers.map(h => h.key);
  } else {
    keys = Object.keys(data[0]) as (keyof T)[];
    csvHeaders = keys.map(key => String(key));
  }

  // Escape CSV values
  const escapeCSVValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  // Build CSV
  const csvRows = [
    csvHeaders.map(escapeCSVValue).join(','), // Header row
    ...data.map(row =>
      keys.map(key => escapeCSVValue(row[key])).join(',')
    )
  ];

  return csvRows.join('\n');
}

/**
 * Downloads data as a CSV file
 * @param data - Array of objects to export
 * @param filename - Name of the file (without extension)
 * @param headers - Optional custom headers
 */
export function exportToCSV<T extends Record<string, any>>(
  data: T[],
  filename: string,
  headers?: { key: keyof T; label: string }[]
): void {
  const csv = convertToCSV(data, headers);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL
  URL.revokeObjectURL(url);
}

/**
 * Formats a date for export
 * @param date - Date string or Date object
 * @param format - Format type ('date' | 'datetime' | 'iso')
 * @returns Formatted date string
 */
export function formatDateForExport(
  date: string | Date,
  format: 'date' | 'datetime' | 'iso' = 'datetime'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';

  switch (format) {
    case 'date':
      return dateObj.toLocaleDateString();
    case 'datetime':
      return dateObj.toLocaleString();
    case 'iso':
      return dateObj.toISOString();
    default:
      return String(date);
  }
}

/**
 * Formats a UTC date string to YYYY/MM/DD format
 * @param dateString - UTC date string (assumes string needs 'Z' appended if not present)
 * @returns Formatted date string in YYYY/MM/DD format
 */
export function formatChangelogDate(dateString: string): string {
  const utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
  const year = utcDate.getFullYear();
  const month = String(utcDate.getMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

/**
 * Formats a UTC date string to YYYY/MM/DD HH:MM:SS format
 * @param dateString - UTC date string (assumes string needs 'Z' appended if not present)
 * @returns Formatted datetime string in YYYY/MM/DD HH:MM:SS format
 */
export function formatChangelogDateTime(dateString: string): string {
  const utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
  const year = utcDate.getFullYear();
  const month = String(utcDate.getMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getDate()).padStart(2, '0');
  const hours = String(utcDate.getHours()).padStart(2, '0');
  const minutes = String(utcDate.getMinutes()).padStart(2, '0');
  const seconds = String(utcDate.getSeconds()).padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Downloads data as JSON file (useful for debugging or full data export)
 * @param data - Data to export
 * @param filename - Name of the file (without extension)
 */
export function exportToJSON<T>(data: T, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');
  
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.json`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}
